import { useEffect, useRef, useState } from 'react';
import type { Socket } from 'socket.io-client';
import type { ClientEvents, ServerEvents } from '../shared/protocol';
import { request } from './socket-request';

type MosaicSocket = Socket<ServerEvents, ClientEvents>;

export function useClockSync(socket: MosaicSocket, active: boolean): number {
  const [offset, setOffset] = useState(0);
  const offsetRef = useRef(0);

  useEffect(() => {
    if (!active || !socket.connected) return;
    let disposed = false;
    const synchronize = async () => {
      const samples: Array<{ offset: number; rtt: number }> = [];
      for (let index = 0; index < 4; index += 1) {
        const sentAt = Date.now();
        try {
          const { serverNow } = await request<{ serverNow: number }>(
            (ack) => socket.emit('time:ping', { sentAt }, ack),
            3_000
          );
          const receivedAt = Date.now();
          samples.push({
            offset: serverNow - (sentAt + (receivedAt - sentAt) / 2),
            rtt: receivedAt - sentAt
          });
        } catch {
          return;
        }
      }
      if (disposed || samples.length === 0) return;
      const best = samples.sort((left, right) => left.rtt - right.rtt)[0];
      const next =
        offsetRef.current === 0 ? best.offset : offsetRef.current * 0.8 + best.offset * 0.2;
      offsetRef.current = next;
      setOffset(next);
    };
    void synchronize();
    const timer = window.setInterval(() => void synchronize(), 30_000);
    return () => {
      disposed = true;
      window.clearInterval(timer);
    };
  }, [active, socket]);

  return offset;
}
